type CompatDb = {
  kind: "dbmaria-firestore-compat";
};

type CollectionRef = {
  kind: "collection";
  path: string;
};

type DocumentRef = {
  kind: "document";
  path: string;
  id: string;
};

const API_BASE = String(
  process.env.REACT_APP_DBMARIA_API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "https://fizmoo.com"
).replace(/\/+$/, "");

export const db: CompatDb = {
  kind: "dbmaria-firestore-compat"
};

export function getFirestore(): CompatDb {
  return db;
}

function joinPath(parts: string[]) {
  return parts.map((part) => String(part || "").trim()).filter(Boolean).join("/");
}

export function collection(parent: CompatDb | DocumentRef, ...segments: string[]): CollectionRef {
  if ((parent as DocumentRef).kind === "document") {
    return {
      kind: "collection",
      path: joinPath([(parent as DocumentRef).path, ...segments])
    };
  }

  return {
    kind: "collection",
    path: joinPath(segments)
  };
}

export function doc(parent: CompatDb | CollectionRef, ...segments: string[]): DocumentRef {
  if ((parent as CollectionRef).kind === "collection") {
    const documentId = String(segments[0] || "").trim();
    return {
      kind: "document",
      path: joinPath([(parent as CollectionRef).path, documentId]),
      id: documentId
    };
  }

  const path = joinPath(segments);
  const parts = path.split("/");
  return {
    kind: "document",
    path,
    id: parts[parts.length - 1] || ""
  };
}

async function buildHeaders(pathValue: string, includeJson = false) {
  const headers = new Headers();
  if (includeJson) headers.set("Content-Type", "application/json");

  if (pathValue.startsWith("users/")) {
    const parts = pathValue.split("/").map((part) => part.trim()).filter(Boolean);
    const userIdFromPath = parts[1] || "";
    if (userIdFromPath) {
      headers.set("x-firestore-compat-user-id", userIdFromPath);
    }
  }

  try {
    const token = window.localStorage.getItem("dbmaria_auth_token");
    if (token) headers.set("Authorization", `Bearer ${token}`);
  } catch {
    // Ignore storage access issues in locked-down contexts.
  }

  return headers;
}

async function readCompatError(response: Response) {
  const raw = await response.text();
  if (!raw) return `Request failed with status ${response.status}`;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.error === "string" && parsed.error.trim()) {
      return parsed.error;
    }
  } catch {
    return raw;
  }

  return raw;
}

function createDocumentSnapshot(id: string, exists: boolean, data: Record<string, any> | null) {
  return {
    id,
    exists: () => exists,
    data: () => (data ? JSON.parse(JSON.stringify(data)) : undefined)
  };
}

function createQuerySnapshot(documents: Array<{ id: string; data: Record<string, any> }>) {
  const docs = documents.map((entry) => createDocumentSnapshot(entry.id, true, entry.data));
  return {
    docs,
    size: docs.length,
    empty: docs.length === 0,
    forEach(callback: (docRef: ReturnType<typeof createDocumentSnapshot>) => void) {
      docs.forEach(callback);
    }
  };
}

export async function getDoc(documentRef: DocumentRef) {
  const headers = await buildHeaders(documentRef.path);
  const response = await fetch(
    `${API_BASE}/api/firestore/doc?path=${encodeURIComponent(documentRef.path)}`,
    {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers
    }
  );

  if (!response.ok) {
    throw new Error(await readCompatError(response));
  }

  const payload = await response.json();
  return createDocumentSnapshot(documentRef.id, Boolean(payload?.exists), payload?.data || null);
}

export async function getDocsFromServer(collectionRef: CollectionRef) {
  const headers = await buildHeaders(collectionRef.path);
  const query = encodeURIComponent(JSON.stringify({ filters: [], orderBy: [], limit: null }));
  const response = await fetch(
    `${API_BASE}/api/firestore/collection?path=${encodeURIComponent(collectionRef.path)}&query=${query}`,
    {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers
    }
  );

  if (!response.ok) {
    throw new Error(await readCompatError(response));
  }

  const payload = await response.json();
  return createQuerySnapshot(Array.isArray(payload?.documents) ? payload.documents : []);
}

export async function setDoc(documentRef: DocumentRef, data: Record<string, any>, options?: { merge?: boolean }) {
  const headers = await buildHeaders(documentRef.path, true);
  const response = await fetch(`${API_BASE}/api/firestore/doc`, {
    method: "PUT",
    cache: "no-store",
    credentials: "include",
    headers,
    body: JSON.stringify({
      path: documentRef.path,
      data,
      merge: Boolean(options?.merge)
    })
  });

  if (!response.ok) {
    throw new Error(await readCompatError(response));
  }
}

export async function addDoc(collectionRef: CollectionRef, data: Record<string, any>) {
  const headers = await buildHeaders(collectionRef.path, true);
  const response = await fetch(`${API_BASE}/api/firestore/collection`, {
    method: "POST",
    cache: "no-store",
    credentials: "include",
    headers,
    body: JSON.stringify({
      path: collectionRef.path,
      data
    })
  });

  if (!response.ok) {
    throw new Error(await readCompatError(response));
  }

  const payload = await response.json();
  return doc(collectionRef, String(payload?.id || ""));
}

export async function deleteDoc(documentRef: DocumentRef) {
  const headers = await buildHeaders(documentRef.path);
  const response = await fetch(
    `${API_BASE}/api/firestore/doc?path=${encodeURIComponent(documentRef.path)}`,
    {
      method: "DELETE",
      cache: "no-store",
      credentials: "include",
      headers
    }
  );

  if (!response.ok) {
    throw new Error(await readCompatError(response));
  }
}
